/**
 * Table of Contents (Spis treści)
 *
 * - Smooth-scrolls to a heading when its TOC link is clicked.
 * - Highlights the link of the section currently in view (scrollspy).
 */
const tableOfContents = () => {
    const toc = document.querySelector(".bn-table-of-contents");

    if (!toc) {
        return;
    }

    const links = Array.from(toc.querySelectorAll("a[href^='#']"));

    if (!links.length) {
        return;
    }

    // Map each link to its target heading.
    const items = links
        .map((link) => {
            const id = decodeURIComponent(link.getAttribute("href").slice(1));
            const target = id ? document.getElementById(id) : null;
            return target ? { link, target, li: link.closest("li") } : null;
        })
        .filter(Boolean);

    if (!items.length) {
        return;
    }

    const OFFSET = 120; // sticky header allowance

    // ── Mobile accordion ──────────────────────────────────────────────────
    // On phones/tablets the TOC sits above the article and collapses behind
    // its title. Desktop is untouched (the collapse CSS is media-scoped).
    const title = toc.querySelector(".bn-table-of-contents__title");
    const mq = window.matchMedia("(max-width: 767px)");
    let collapsibleOn = false;

    const setExpanded = (open) => {
        toc.classList.toggle("is-open", open);
        if (title) {
            title.setAttribute("aria-expanded", open ? "true" : "false");
        }
    };

    const enableCollapsible = () => {
        if (collapsibleOn || !title) {
            return;
        }
        collapsibleOn = true;
        toc.classList.add("bn-table-of-contents--collapsible");
        title.setAttribute("role", "button");
        title.setAttribute("tabindex", "0");
        setExpanded(false); // collapsed by default on mobile
    };

    const disableCollapsible = () => {
        if (!collapsibleOn) {
            return;
        }
        collapsibleOn = false;
        toc.classList.remove("bn-table-of-contents--collapsible", "is-open");
        if (title) {
            title.removeAttribute("role");
            title.removeAttribute("tabindex");
            title.removeAttribute("aria-expanded");
        }
    };

    const syncCollapsible = (matches) =>
        matches ? enableCollapsible() : disableCollapsible();

    if (title) {
        const toggle = () =>
            collapsibleOn && setExpanded(!toc.classList.contains("is-open"));
        title.addEventListener("click", toggle);
        title.addEventListener("keydown", (event) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                toggle();
            }
        });
    }

    syncCollapsible(mq.matches);
    // matchMedia: addEventListener where supported, addListener as fallback.
    if (mq.addEventListener) {
        mq.addEventListener("change", (event) => syncCollapsible(event.matches));
    } else if (mq.addListener) {
        mq.addListener((event) => syncCollapsible(event.matches));
    }

    // Smooth scroll on click.
    items.forEach(({ link, target }) => {
        link.addEventListener("click", (event) => {
            event.preventDefault();
            const top =
                target.getBoundingClientRect().top + window.pageYOffset - OFFSET;
            window.scrollTo({ top, behavior: "smooth" });
            history.replaceState(null, "", link.getAttribute("href"));
            // On mobile, collapse the accordion after picking a section.
            if (collapsibleOn) {
                setExpanded(false);
            }
        });
    });

    const setActive = (activeLi) => {
        items.forEach(({ li }) => {
            if (li) {
                li.classList.toggle("is-active", li === activeLi);
            }
        });
    };

    // Absolute document offset of an element (offsetTop is relative to the
    // offsetParent, which breaks once headings are nested in flex wrappers).
    const absoluteTop = (el) =>
        el.getBoundingClientRect().top + window.pageYOffset;

    // Scrollspy: pick the last heading scrolled past.
    const onScroll = () => {
        const scrollPos = window.pageYOffset + OFFSET + 10;
        let current = items[0];

        items.forEach((item) => {
            if (absoluteTop(item.target) <= scrollPos) {
                current = item;
            }
        });

        setActive(current.li);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
};

module.exports = { tableOfContents };
